/**
 * Admin Shipping panel — Starshipit / Australia Post label ops.
 *
 * Feb 2026 — REFACTORED for two-label workflow.
 * Starshipit confirmed that a paid posted-kit booking needs TWO
 * separate Starshipit orders (Starshipit cannot generate two labels
 * from one order for MyPost Business). This panel now renders TWO
 * labelled sections — one per Starshipit order:
 *
 *   • Outbound Kit Label            {ref}-OUT           B20 (Express — Jun 2026, was B30 Standard)
 *     (Lab → Customer — sticks on the outside of the kit)
 *   • Sample Return Express Label   {ref}-SAMPLE-RETURN B20 (Express)
 *     (Customer → Lab — enclosed inside the kit so the customer can
 *     drop their sample in any AusPost box)
 *
 * Both labels are paid by our Australia Post account and printed by
 * us using the Starshipit Desktop App. This is NOT an Australia Post
 * formal return-label workflow — the sample-return is just a normal
 * Express Post order with sender / receiver reversed.
 *
 * Actions per section (admin-only):
 *   • Create      → POST /api/admin/bookings/{ref}/shipping/starshipit/create-label?kind=outbound|sample_return
 *   • Retry       → POST .../retry?kind=outbound|sample_return
 *   • Reprint     → POST .../reprint?kind=outbound|sample_return
 *   • Open in Starshipit — external link (if URL/order id present)
 *
 * Plus a top-level "Create Both Labels" that fires both at once
 * (POST .../create-label with no kind param, or kind=both).
 *
 * The panel never exposes API keys — those live purely in backend
 * env vars. The banner surfaces test-mode + credential status so the
 * admin knows whether they're firing a live label or a dry-run.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Truck, Printer, RefreshCw, ExternalLink, AlertTriangle,
  Loader2, CheckCircle2, ShieldAlert, Info, Copy, ArrowRightLeft,
  Package, PackageCheck, RotateCcw, Check,
} from "lucide-react";
import api from "@/lib/api";
import { trackingLink } from "@/lib/carrierTracking";

// ─── Status vocabulary ─────────────────────────────────────────────
const STATUS_META = {
  not_required:            { label: "Not required",           tone: "slate"  },
  pending:                 { label: "Pending",                 tone: "amber"  },
  awaiting_fulfilment:     { label: "Awaiting fulfilment",     tone: "amber"  },
  in_progress:             { label: "In progress",             tone: "amber"  },
  order_created:           { label: "Order created",           tone: "cyan"   },
  label_created:           { label: "Label created",           tone: "cyan"   },
  print_requested:         { label: "Print queued",            tone: "cyan"   },
  printed:                 { label: "Printed",                 tone: "green"  },
  manifested:              { label: "Manifested",              tone: "green"  },
  dispatched:              { label: "Dispatched",              tone: "green"  },
  in_transit:              { label: "In transit",              tone: "green"  },
  out_for_delivery:        { label: "Out for delivery",        tone: "green"  },
  delivered:               { label: "Delivered",               tone: "green"  },
  complete:                { label: "Complete",                tone: "green"  },
  exception:               { label: "Exception",               tone: "amber"  },
  failed:                  { label: "Failed",                  tone: "rose"   },
  partial_failed:          { label: "One label failed",        tone: "rose"   },
  manual_action_required:  { label: "Manual action required",  tone: "rose"   },
};

const TONE_CLASS = {
  slate:  "bg-slate-100 text-slate-700 border-slate-200",
  amber:  "bg-amber-50 text-amber-800 border-amber-200",
  cyan:   "bg-cyan-50 text-cyan-800 border-cyan-200",
  green:  "bg-emerald-50 text-emerald-800 border-emerald-200",
  rose:   "bg-rose-50 text-rose-800 border-rose-200",
};

const SUCCESS_STATUSES = new Set([
  "label_created", "print_requested", "printed", "manifested",
  "dispatched", "in_transit", "out_for_delivery", "delivered",
]);

// ─── Presentational bits ───────────────────────────────────────────
function StatusPill({ status, testId }) {
  const meta = STATUS_META[status] || { label: status || "—", tone: "slate" };
  return (
    <span
      data-testid={testId || "shipping-status-pill"}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${TONE_CLASS[meta.tone]}`}
    >
      <Truck className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function Row({ label, children, testId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px,1fr] gap-1 sm:gap-3 py-1.5 text-[13px]">
      <div className="text-slate-500 font-medium">{label}</div>
      <div className="text-slate-900 break-words" data-testid={testId}>
        {children || <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}

// Known carrier-error patterns → plain-English operator guidance.
function errorHint(err) {
  const e = (err || "").toLowerCase();
  if (e.includes("po box") || e.includes("parcel locker")) {
    return "Aramex is a courier and can't deliver to PO Boxes or Parcel Lockers. Click \"Regenerate as Express\" below — it voids the Aramex order and re-creates this label as AusPost Express Post, which does deliver to PO Boxes and Parcel Lockers.";
  }
  if (e.includes("no account id")) {
    return "Australia Post rejected the request because Starshipit has no active AusPost account linked. In Starshipit go to Settings → Couriers → Australia Post and reconnect it: check the account number (some accounts need 000 prefixed), verify the API key/secret, or re-authorise MyPost Business and select the charge account. Then click Retry here. For the OUTBOUND kit label you can also click \"Back to Aramex\" below to sidestep AusPost entirely (the sample-return label must stay AusPost Express, so the connection still needs fixing).";
  }
  if (e.includes("fin_cr_low") || (e.includes("aramex") && (e.includes("does not have") || e.includes("insufficient")))) {
    return "Aramex declined this consignment for insufficient available credit (code FIN_CR_LOW) — usually because Aramex quoted an unusually high rate for this address (e.g. remote areas). The system automatically retries these as AusPost Express Post; if this error is still showing, the automatic fallback also failed — check the AusPost connection, or click \"Regenerate as Express\" to retry it manually.";
  }
  if (e.includes("no tracking number")) {
    return "Starshipit accepted the order but the carrier silently rejected it — usually a broken carrier connection in Starshipit (expired credentials or missing product entitlement). Click \"Open Starshipit\" above (it copies the order reference — paste it into Starshipit's search) to see the carrier's own error, fix the connection under Starshipit Settings → Couriers, then Retry here. The raw carrier response is in the diagnostic section below.";
  }
  return null;
}

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Could not copy — try selecting manually.");
  }
}

// ─── Per-kind section ──────────────────────────────────────────────
function LabelSection({
  kind, title, subtitle, Icon, state, busy, onAction, shippingRequired,
  rateCheck, rateCheckBusy, onRunRateCheck,
}) {
  const prefix = kind;                       // "outbound" | "sample_return"
  const orderId       = state[`${prefix}_starshipit_order_id`];
  const orderRef      = state[`${prefix}_order_reference`];
  const trackingNum   = state[`${prefix}_tracking_number`];
  const trackingUrl   = state[`${prefix}_tracking_url`];
  const labelUrl      = state[`${prefix}_label_url`];
  const labelStatus   = state[`${prefix}_label_status`];
  const printStatus   = state[`${prefix}_print_status`];
  const serviceCode   = state[`${prefix}_service_code`];
  const labelCreated  = state[`${prefix}_label_created_at`];
  const lastError     = state[`${prefix}_last_error`];
  const lastErrorAt   = state[`${prefix}_last_error_at`];
  // A consignment number Starshipit returned but an old carrier check
  // refused — offer to accept it rather than re-buying the label.
  const refusedTracking = !trackingNum
    ? (state[`${prefix}_raw_response`] || {}).rejected_tracking || null
    : null;
  // Sep 2026 — an error that a later retry/webhook superseded. Shown as
  // history, never as an active failure, and never offers "Retry".
  const resolvedError   = state[`${prefix}_resolved_error`];
  const resolvedErrorAt = state[`${prefix}_resolved_error_at`];
  const lastRetryAt   = state[`${prefix}_last_retry_at`];

  // Feb 2026 (SCWT-RETRY-COOLDOWN) — client-side echo of the backend
  // 60s rate-limit. Backend is the source of truth (returns 429); this
  // just prevents obvious double-clicks and shows a countdown.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!lastRetryAt) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [lastRetryAt]);
  const secondsSinceRetry = lastRetryAt
    ? Math.max(0, Math.floor((now - new Date(lastRetryAt).getTime()) / 1000))
    : Infinity;
  const cooldownRemaining = Math.max(0, 60 - secondsSinceRetry);
  const inCooldown = cooldownRemaining > 0;

  const canCreate  = shippingRequired && !orderId;
  const canRetry   = (!!lastError || labelStatus === "failed" || labelStatus === "manual_action_required") && !inCooldown;
  const canReprint = !!orderId;
  // Jun 2026 — outbound labels created before the Express-both-ways
  // change are locked to B30; offer a one-click Express regeneration
  // until the kit is actually moving through AusPost.
  const SHIPPED = new Set(["manifested", "dispatched", "in_transit", "out_for_delivery", "delivered"]);
  const canRegenExpress = prefix === "outbound" && !!orderId
    && !!serviceCode && serviceCode !== "B20" && !SHIPPED.has(labelStatus);
  // Sep 2026 — reverse path: an outbound leg pinned to Express (after an
  // Aramex fallback) can be switched back to Aramex once credit/carrier
  // is healthy again — vital when the AusPost link itself is broken.
  const canRegenAramex = prefix === "outbound" && !!orderId
    && serviceCode === "B20" && !SHIPPED.has(labelStatus);

  // Sep 2026 — one carrier-aware helper for every screen: the kit ships
  // Aramex (MP…/AN… consignments) and the sample returns AusPost Express.
  const track = trackingLink({
    trackingNumber: trackingNum, trackingUrl, kind,
    carrier: state[`${prefix}_carrier`],
    carrierName: state[`${prefix}_carrier_name`],
    serviceCode,
  });
  // Starshipit has NO public per-order deep link (a /orders/{id} URL 404s).
  // Link to the orders grid and copy the reference so ops can paste it
  // straight into Starshipit's search box.
  const starshipitOrdersUrl = "https://app.starshipit.com/";

  const kindBusy = (a) => busy === `${a}:${prefix}`;
  const anyBusy = !!busy;

  // Nov 2026 — Aramex fallback visibility (approved enhancement).
  // Backend-computed, outbound-only; see `outbound_carrier_transparency`.
  const carrierCheck = prefix === "outbound" ? state.outbound_carrier_check : null;

  const tone = SUCCESS_STATUSES.has(labelStatus) ? "border-emerald-200 bg-emerald-50/30"
    : (labelStatus === "failed" || labelStatus === "manual_action_required") ? "border-rose-200 bg-rose-50/30"
    : labelStatus ? "border-cyan-200 bg-cyan-50/30"
    : "border-slate-200 bg-slate-50/30";

  return (
    <div
      data-testid={`shipping-label-section-${prefix}`}
      className={`rounded-2xl border p-4 sm:p-5 ${tone}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-slate-700 flex-shrink-0" />
            <h4
              data-testid={`shipping-label-title-${prefix}`}
              className="font-display text-base font-semibold text-slate-900"
            >
              {title}
            </h4>
          </div>
          <div className="text-[12px] text-slate-600 mt-0.5">{subtitle}</div>
        </div>
        <StatusPill status={labelStatus || "pending"} testId={`shipping-status-pill-${prefix}`} />
      </div>

      {/* Nov 2026 — Aramex fallback visibility: requested vs actual
          carrier, fallback reason + timestamp, always visible (no
          Starshipit call — computed from fields already on the
          booking). Outbound leg only. */}
      {carrierCheck && (carrierCheck.is_fallback || carrierCheck.guard_blocked) && (
        <div
          className={`mb-3 rounded-xl border p-3 text-[12px] ${
            carrierCheck.guard_blocked
              ? "border-rose-200 bg-rose-50 text-rose-800"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
          data-testid="shipping-carrier-fallback-banner"
        >
          <div className="flex flex-wrap items-center gap-1.5 font-semibold mb-1">
            <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" />
            {carrierCheck.guard_blocked ? "Carrier mismatch — label blocked" : "Carrier switched"}
          </div>
          {carrierCheck.guard_blocked ? (
            <>
              <p data-testid="shipping-carrier-guard-detail">
                Requested <strong>{carrierCheck.guard_detail?.requested_carrier || "Aramex"}</strong>, but
                Starshipit tried to return <strong>{carrierCheck.guard_detail?.returned_carrier || "a different carrier"}</strong>
                {carrierCheck.guard_detail?.returned_service ? ` (${carrierCheck.guard_detail.returned_service})` : ""} outside the
                normal credit-fallback path — the guard refused this label rather than silently ship it on the wrong carrier.
              </p>
              <p className="mt-1 text-[11px] opacity-80">Use Retry below, or Carrier diagnostic to check live entitlement.</p>
            </>
          ) : (
            <>
              <p>
                <span data-testid="shipping-carrier-requested">Requested: <strong>{carrierCheck.requested_carrier}</strong></span>
                {" · "}
                <span data-testid="shipping-carrier-actual">Actual: <strong>{carrierCheck.actual_carrier}</strong></span>
              </p>
              <p className="mt-1" data-testid="shipping-carrier-fallback-reason">{carrierCheck.fallback_reason}</p>
              {carrierCheck.fallback_at && (
                <p className="mt-1 text-[11px] opacity-80" data-testid="shipping-carrier-fallback-at">
                  {new Date(carrierCheck.fallback_at).toLocaleString("en-AU")}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Detail rows */}
      <div className="border-t border-slate-200/60 mt-2 pt-2">
        <Row label="Order reference" testId={`shipping-order-ref-${prefix}`}>
          {orderRef ? (
            <span className="inline-flex items-center gap-2">
              <span className="font-mono text-[12px]">{orderRef}</span>
              <button onClick={() => copyToClipboard(orderRef)} className="text-slate-400 hover:text-slate-700">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </span>
          ) : null}
        </Row>
        <Row label="Service" testId={`shipping-service-code-${prefix}`}>
          {serviceCode ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-mono text-[12px] px-1.5 py-0.5 rounded bg-white border border-slate-200">{serviceCode}</span>
              <span className="text-[12px] text-slate-500">
                {serviceCode === "B20" ? "Express Post" : serviceCode === "B30" ? "Standard Post" : ""}
              </span>
            </span>
          ) : null}
        </Row>
        <Row label="Starshipit order" testId={`shipping-order-id-${prefix}`}>
          {orderId ? (
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[12px]">{orderId}</span>
              <button onClick={() => copyToClipboard(orderId)} className="text-slate-400 hover:text-slate-700">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <a
                href={starshipitOrdersUrl}
                target="_blank" rel="noreferrer noopener"
                onClick={() => copyToClipboard(orderRef || orderId)}
                title="Starshipit has no direct order links — this copies the order reference and opens Starshipit; paste it into their search box."
                className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 text-[12px] font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open Starshipit (copies ref)
              </a>
            </span>
          ) : null}
        </Row>
        <Row label="Tracking number" testId={`shipping-tracking-number-${prefix}`}>
          {trackingNum ? (
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[12px]">{trackingNum}</span>
              <button onClick={() => copyToClipboard(trackingNum)} className="text-slate-400 hover:text-slate-700">
                <Copy className="w-3.5 h-3.5" />
              </button>
              {track?.href ? (
                <a
                  href={track.href}
                  target="_blank" rel="noreferrer noopener"
                  title={track.title}
                  data-testid={`shipping-tracking-link-${prefix}`}
                  className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 text-[12px] font-medium"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Track with {track.carrierLabel}
                </a>
              ) : (
                <span data-testid={`shipping-tracking-unknown-carrier-${prefix}`}
                  className="text-[11px] text-amber-700">
                  carrier unknown — no tracking link
                </span>
              )}
            </span>
          ) : null}
        </Row>
        <Row label="Print status" testId={`shipping-print-status-${prefix}`}>
          {printStatus || null}
        </Row>
        <Row label="Label URL" testId={`shipping-label-url-${prefix}`}>
          {labelUrl ? (
            <a
              href={labelUrl}
              target="_blank" rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 text-[12px] font-medium"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Download label PDF
            </a>
          ) : null}
        </Row>
        <Row label="Label created">{labelCreated || null}</Row>
        {lastError && (
          <Row label="Last error" testId={`shipping-last-error-${prefix}`}>
            <div className="inline-flex items-start gap-2 text-rose-800">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              <span>
                <span className="block font-medium">{lastError}</span>
                {lastErrorAt && (
                  <span className="text-[11px] text-rose-600">{lastErrorAt}</span>
                )}
              </span>
            </div>
          </Row>
        )}
        {lastError && errorHint(lastError) && (
          <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900" data-testid={`shipping-error-hint-${prefix}`}>
            <span className="font-bold">What to do: </span>{errorHint(lastError)}
          </div>
        )}
        {!lastError && resolvedError && (
          <Row label="Previous error" testId={`shipping-resolved-error-${prefix}`}>
            <div className="inline-flex items-start gap-2 text-slate-500">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600" />
              <span>
                <span className="block font-medium text-slate-600">
                  Resolved after retry/sync
                </span>
                <span className="block text-[11px]">{resolvedError}</span>
                {resolvedErrorAt && (
                  <span className="text-[11px] text-slate-400">{resolvedErrorAt}</span>
                )}
              </span>
            </div>
          </Row>
        )}
        {(lastError || resolvedError) && state[`${prefix}_raw_response`] && (
          <details className="mt-2" data-testid={`shipping-raw-response-${prefix}`}>
            <summary className="cursor-pointer text-[11px] font-semibold text-slate-500 hover:text-slate-700">
              Carrier diagnostic (raw Starshipit response)
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-slate-900 text-slate-100 text-[10px] p-3 whitespace-pre-wrap break-all">
              {JSON.stringify(state[`${prefix}_raw_response`], null, 2)}
            </pre>
          </details>
        )}
      </div>

      {/* Actions */}
      <div className="mt-3 pt-3 border-t border-slate-200/60 flex flex-wrap gap-2">
        {/* Nov 2026 — before a label is purchased, let staff see the
            live Aramex vs Express Post price difference for THIS
            destination. Read-only Starshipit rate lookup, never an
            order/shipment. Outbound-only, and only while there's no
            label yet (once a label exists the carrier is locked in). */}
        {prefix === "outbound" && canCreate && (
          <button
            type="button"
            onClick={onRunRateCheck}
            disabled={rateCheckBusy || anyBusy}
            data-testid="shipping-outbound-rate-check-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-800 text-[12px] font-semibold px-3 py-1.5"
          >
            {rateCheckBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
            Check price difference
          </button>
        )}
        {/* Sep 2026 — recover a consignment the old carrier guard wrongly
            refused (MP…/AN… are Aramex, not AusPost). Accepts the number
            Starshipit already returned instead of buying a new label. */}
        {refusedTracking && (
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => {
              if (window.confirm(
                `Accept tracking ${refusedTracking} for the ${title}?\n\n`
                + "This is the consignment number Starshipit returned. It was "
                + "refused by an old carrier check. No new label is created.",
              )) onAction("adopt-tracking", prefix);
            }}
            data-testid={`shipping-adopt-tracking-btn-${prefix}`}
            title={`Accept ${refusedTracking} and clear this failure`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white text-[12px] font-semibold px-3 py-1.5"
          >
            {kindBusy("adopt-tracking")
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Check className="w-3.5 h-3.5" />}
            Accept {refusedTracking}
          </button>
        )}
        <button
          type="button"
          disabled={!canCreate || anyBusy}
          onClick={() => onAction("create-label", prefix)}
          data-testid={`shipping-create-btn-${prefix}`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[12px] font-semibold px-3 py-1.5"
        >
          {kindBusy("create-label") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
          Create
        </button>
        <button
          type="button"
          disabled={!canRetry || anyBusy || inCooldown}
          onClick={() => onAction("retry", prefix)}
          data-testid={`shipping-retry-btn-${prefix}`}
          title={inCooldown ? `Cooldown active — try again in ${cooldownRemaining}s` : undefined}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-800 text-[12px] font-semibold px-3 py-1.5"
        >
          {kindBusy("retry") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {inCooldown ? `Retry (${cooldownRemaining}s)` : "Retry"}
        </button>
        <button
          type="button"
          disabled={!canReprint || anyBusy}
          onClick={() => onAction("reprint", prefix)}
          data-testid={`shipping-reprint-btn-${prefix}`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-800 text-[12px] font-semibold px-3 py-1.5"
        >
          {kindBusy("reprint") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
          Reprint
        </button>
        {(orderId || labelStatus) && !SHIPPED.has(labelStatus) && (
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => {
              if (window.confirm(
                `Reset the ${title} label state?\n\nThe existing Starshipit order will be archived and this booking returns to "pending" so you can create a fresh label. Use for testing / re-doing a wrong label.`,
              )) onAction("reset", prefix);
            }}
            data-testid={`shipping-reset-btn-${prefix}`}
            title="Archive the current Starshipit order and clear the label state so Create can be run again"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-rose-300 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed text-rose-700 text-[12px] font-semibold px-3 py-1.5"
          >
            {kindBusy("reset") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Reset
          </button>
        )}
        {canRegenExpress && (
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => onAction("regenerate-express", prefix)}
            data-testid={`shipping-regenerate-express-btn-${prefix}`}
            title="Voids this Parcel Post order and creates a fresh Express Post (B20) label"
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[12px] font-semibold px-3 py-1.5"
          >
            {kindBusy("regenerate-express") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
            Regenerate as Express
          </button>
        )}
        {canRegenAramex && (
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => {
              if (window.confirm(
                "Switch this outbound label back to Aramex?\n\nThe current Express Post order will be voided and a fresh Aramex order created. Use this once Aramex credit/connection is healthy again (e.g. when AusPost is the carrier having problems).",
              )) onAction("regenerate-aramex", prefix);
            }}
            data-testid={`shipping-regenerate-aramex-btn-${prefix}`}
            title="Voids this Express Post order and re-creates the outbound label on Aramex (the default outbound carrier)"
            className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[12px] font-semibold px-3 py-1.5"
          >
            {kindBusy("regenerate-aramex") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
            Back to Aramex
          </button>
        )}
      </div>

      {/* Nov 2026 — price-difference results (read-only Starshipit
          rate lookup; shown before the customer's label is purchased). */}
      {prefix === "outbound" && rateCheck && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12px]" data-testid="shipping-outbound-rate-check-results">
          {rateCheck.ok === false ? (
            <p className="text-slate-600" data-testid="shipping-rate-check-unavailable">
              {rateCheck.status === 403
                ? "Starshipit refused the rate-lookup endpoint (HTTP 403) — this API subscription doesn't include the rates scope, so a live price comparison isn't available right now."
                : rateCheck.status === "no_credentials"
                ? "Starshipit API keys are missing in this environment."
                : rateCheck.status === "booking_not_found"
                ? "Couldn't find this booking — try refreshing the page."
                : rateCheck.status === "invalid_destination"
                ? `This booking's delivery address isn't complete enough for a rate quote${rateCheck.error ? `: ${rateCheck.error}` : "."}`
                : "Couldn't get a live price comparison right now — try again shortly."}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <span data-testid="shipping-rate-aramex-quote">
                  Aramex: <strong>{rateCheck.aramex_quote?.total_price != null ? `$${Number(rateCheck.aramex_quote.total_price).toFixed(2)}` : "—"}</strong>
                </span>
                <span data-testid="shipping-rate-express-quote">
                  Express Post: <strong>{rateCheck.express_quote?.total_price != null ? `$${Number(rateCheck.express_quote.total_price).toFixed(2)}` : "—"}</strong>
                </span>
              </div>
              {rateCheck.price_difference != null ? (
                <p data-testid="shipping-rate-price-difference">
                  Switching to Express Post would {rateCheck.price_difference >= 0 ? "cost" : "save"} <strong>${Math.abs(rateCheck.price_difference).toFixed(2)}</strong> {rateCheck.price_difference >= 0 ? "more" : ""} than Aramex for this address.
                </p>
              ) : (
                <p className="text-slate-600">Couldn't match both carriers' quotes for this address.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Feb 2026 (SCWT-CARRIER-HEALTH) — subtle status chip that surfaces
// Starshipit → AusPost credential health so ops can see when the
// entitlement issue is over without digging through logs.
function CarrierHealthChip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/starshipit/carrier-health${force ? "?force=true" : ""}`);
      setData(data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(iv);
  }, []);

  if (!data) {
    return (
      <span data-testid="carrier-health-chip-loading"
            className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold px-2.5 py-1 bg-slate-100 text-slate-500">
        <Loader2 className="w-3 h-3 animate-spin" /> Carrier check…
      </span>
    );
  }
  const s = data.status;
  const tone = s === "ok" ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
             : s === "warn" ? "bg-amber-100 text-amber-800 border border-amber-200"
             : s === "test_mode" ? "bg-slate-100 text-slate-600 border border-slate-200"
             : "bg-rose-100 text-rose-800 border border-rose-200";
  const dot  = s === "ok" ? "bg-emerald-500"
             : s === "warn" ? "bg-amber-500"
             : s === "test_mode" ? "bg-slate-400"
             : "bg-rose-500";
  const label = s === "ok" ? "Starshipit OK"
              : s === "warn" ? "Starshipit warn"
              : s === "test_mode" ? "Dry-run"
              : "Starshipit error";
  const secondsAgo = data.checked_at ? Math.floor((now - new Date(data.checked_at).getTime()) / 1000) : null;
  const ago = secondsAgo == null ? ""
            : secondsAgo < 60 ? `${secondsAgo}s ago`
            : secondsAgo < 3600 ? `${Math.floor(secondsAgo / 60)}m ago`
            : `${Math.floor(secondsAgo / 3600)}h ago`;
  return (
    <button
      type="button"
      data-testid="carrier-health-chip"
      onClick={() => load(true)}
      title={data.last_error || `${label} · checked ${ago}${data.cache_hit ? " (cached)" : ""} · click to re-check`}
      disabled={loading}
      className={`inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold px-2.5 py-1 ${tone} hover:brightness-95 disabled:opacity-70`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot} ${s === "ok" ? "animate-pulse" : ""}`} />
      <span data-testid="carrier-health-chip-label">{label}</span>
      {ago && <span className="text-[10px] opacity-70">· {ago}</span>}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main panel
// ═══════════════════════════════════════════════════════════════════
export default function StarshipitShippingPanel({ reference, refreshKey }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "<action>:<kind>" or "create-label:both"
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  // Nov 2026 — Aramex fallback visibility: live Aramex vs Express Post
  // price comparison for the OUTBOUND leg, fetched on demand (never on
  // page load — it's a real Starshipit API call) so staff can see the
  // price difference BEFORE choosing to switch carrier or create a label.
  const [rateCheck, setRateCheck] = useState(null);
  const [rateCheckBusy, setRateCheckBusy] = useState(false);

  const runRateCheck = async () => {
    setRateCheckBusy(true);
    try {
      const { data } = await api.get(`/admin/bookings/${reference}/shipping/starshipit/outbound-rate-check`);
      setRateCheck(data);
    } catch (e) {
      setRateCheck({ ok: false, status: "request_failed", services: [], error: e.response?.data || String(e) });
    } finally {
      setRateCheckBusy(false);
    }
  };

  const runDiag = async () => {
    setDiagBusy(true);
    try {
      const { data } = await api.get(`/admin/starshipit/delivery-services?reference=${encodeURIComponent(reference)}`);
      setDiag(data);
    } catch (e) {
      setDiag({ status: "request_failed", services: [], raw: e.response?.data || String(e) });
    } finally {
      setDiagBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings/${reference}/shipping`);
      setState(data);
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [reference]);

  // Nov 2026 (SCWT-34E6D6) — the posted-kit address editor lives in a
  // separate panel above this one; bump `refreshKey` from there after a
  // successful save so this panel's eligibility banner/buttons update
  // without requiring a manual page reload.
  useEffect(() => { load(); }, [load, refreshKey]);

  const runAction = async (action, kind /* "outbound" | "sample_return" | "both" */) => {
    setBusy(`${action}:${kind}`);
    try {
      const url = `/admin/bookings/${reference}/shipping/starshipit/${action}${kind ? `?kind=${kind}` : ""}`;
      const { data } = await api.post(url);
      // Aug 2026 — reset returns its own flat {ok, results} shape.
      if (action === "reset") {
        if (data.ok) {
          toast.success("Label state reset — you can create a fresh label now");
        } else {
          const reasons = Object.values(data.results || {})
            .map((r) => r?.reason).filter(Boolean).join(", ");
          toast.error(`Reset failed — ${reasons || "unknown"}`);
        }
        await load();
        return;
      }
      // Sep 2026 — adopt-tracking returns its own flat shape.
      if (action === "adopt-tracking") {
        toast.success(`Tracking ${data.tracking_number} accepted — label marked created`);
        await load();
        return;
      }
      // Jun 2026 — regenerate-express returns its own flat shape.
      if (action === "regenerate-express" || action === "regenerate-aramex") {
        const label = action === "regenerate-aramex" ? "Aramex" : "Express Post";
        if (data.ok) {
          toast.success(`Outbound label regenerated as ${label} (${data.new_service_code || (action === "regenerate-aramex" ? "Aramex" : "B20")})${data.test_mode ? " (dry-run)" : ""}`);
        } else {
          toast.error(`Regenerate failed — ${data.reason || "unknown"}`);
        }
        await load();
        return;
      }
      // The response has {outbound, sample_return, overall_status, ...}
      const parts = [];
      if (data.outbound) parts.push(`out=${data.outbound.status}`);
      if (data.sample_return) parts.push(`ret=${data.sample_return.status}`);
      const testTag = data.test_mode ? " (dry-run)" : "";
      if (data.overall_status === "failed" || data.overall_status === "partial_failed") {
        toast.error(`Starshipit ${action} → ${data.overall_status || "error"}${testTag} · ${parts.join(", ")}`);
      } else {
        toast.success(`Starshipit ${action} → ${data.overall_status || data.status}${testTag} · ${parts.join(", ")}`);
      }
      await load();
    } catch (e) {
      // Feb 2026 (SCWT-RETRY-COOLDOWN) — surface the 60s cooldown
      // clearly instead of a generic error toast.
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429 && detail && typeof detail === "object" && detail.code === "retry_cooldown") {
        toast.warning(detail.message || `Retry cooldown — try again in ${detail.retry_after_seconds}s`);
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || `Starshipit ${action} failed`);
      }
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading shipping state…
        </div>
      </section>
    );
  }
  if (!state) return null;

  // Self-hide: only render for mail_out bookings, OR if we already have Starshipit history.
  const hasHistory =
       state.outbound_starshipit_order_id
    || state.sample_return_starshipit_order_id
    || state.outbound_last_error
    || state.sample_return_last_error
    || state.starshipit_order_id
    || state.starshipit_last_error;
  if (!state.shipping_required && !hasHistory) return null;

  const overall = state.shipping_integration_status || "pending";
  const eitherBusy = !!busy;
  const postedKitBlocked = state.shipping_required && state.posted_kit_eligible === false;
  const labelShippingRequired = state.shipping_required && !postedKitBlocked;
  const bothReady = labelShippingRequired
    && !state.outbound_starshipit_order_id
    && !state.sample_return_starshipit_order_id;

  return (
    <section
      data-testid="shipping-panel"
      className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
    >
      {/* Panel header */}
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
            Shipping · Starshipit (Aramex outbound · AusPost Express return)
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <Truck className="w-5 h-5 text-cyan-600" /> Two-label posted-kit workflow
          </h3>
          <p className="text-[12px] text-slate-500 mt-1 max-w-2xl">
            Print outbound label → stick it on the outside of the kit.
            Print sample-return Express label → place it INSIDE the kit
            so the customer can post their sample back to the lab.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <CarrierHealthChip />
          <StatusPill status={overall} testId="shipping-status-pill" />
        </div>
      </div>

      {/* Mode / credential banner */}
      <div
        data-testid="shipping-mode-banner"
        className={`mb-4 rounded-lg border px-3 py-2 flex items-start gap-2 text-[12px] ${
          !state.starshipit_credentialed
            ? "bg-rose-50 border-rose-200 text-rose-900"
            : state.starshipit_test_mode
              ? "bg-amber-50 border-amber-200 text-amber-900"
              : "bg-emerald-50 border-emerald-200 text-emerald-900"
        }`}
      >
        {!state.starshipit_credentialed
          ? <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          : state.starshipit_test_mode
            ? <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            : <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />}
        <div>
          {!state.starshipit_credentialed && (
            <>
              <span className="font-semibold">Starshipit API keys missing.</span>{" "}
              Set <code className="font-mono text-[11px] bg-white/60 px-1 rounded">STARSHIPIT_API_KEY</code>{" "}
              and <code className="font-mono text-[11px] bg-white/60 px-1 rounded">STARSHIPIT_SUBSCRIPTION_KEY</code>{" "}
              in the backend .env — until they are set, all actions run in dry-run mode.
            </>
          )}
          {state.starshipit_credentialed && state.starshipit_test_mode && (
            <>
              <span className="font-semibold">Dry-run mode.</span>{" "}
              Actions on this booking will stamp synthetic tracking without hitting Starshipit or Australia Post. Flip
              {" "}<code className="font-mono text-[11px] bg-white/60 px-1 rounded">STARSHIPIT_TEST_MODE=false</code> in
              .env once you&rsquo;re ready to create live labels.
            </>
          )}
          {state.starshipit_credentialed && !state.starshipit_test_mode && (
            <>
              <span className="font-semibold">Live mode.</span>{" "}
              Actions can create real shipping labels through Starshipit (Aramex outbound · Australia Post Express return) and queue them for the Starshipit Desktop App.
            </>
          )}
          {!state.starshipit_flag_enabled && (
            <div className="mt-1">
              <span className="font-semibold">Feature flag <code className="font-mono text-[11px] bg-white/60 px-1 rounded">starshipit_shipping</code> is disabled.</span>{" "}
              Toggle it on <a href="/admin/settings/features" className="underline">Feature Flags</a> before creating labels.
            </div>
          )}
        </div>
      </div>

      {/* Nov 2026 (SCWT-34E6D6) — posted-kit eligibility banner. Shown
          instead of a confusing post-click rejection when this is a
          paid mail-out booking the backend isn't ready to label yet. */}
      {state.shipping_required && state.posted_kit_eligible === false && (
        <div
          data-testid="shipping-posted-kit-ineligible-banner"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-start gap-2 text-[12px] text-amber-900"
        >
          <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">{state.posted_kit_staff_message}</div>
            {state.posted_kit_repair_hint && (
              <div className="mt-1">{state.posted_kit_repair_hint}</div>
            )}
          </div>
        </div>
      )}

      {/* Combined "Create Both Labels" — only when neither exists yet */}
      {bothReady && (
        <div className="mb-4">
          <button
            type="button"
            disabled={eitherBusy}
            onClick={() => runAction("create-label", "both")}
            data-testid="shipping-create-both-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-semibold px-4 py-2.5 shadow-sm"
          >
            {busy === "create-label:both"
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <ArrowRightLeft className="w-4 h-4" />}
            Create Both Labels
          </button>
        </div>
      )}

      {/* Two-label grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <LabelSection
          kind="outbound"
          title="Outbound Kit Label"
          subtitle="Aramex · Lab → Customer · sticks on the outside of the mailed kit"
          Icon={Package}
          state={state}
          busy={busy}
          onAction={runAction}
          shippingRequired={labelShippingRequired}
          rateCheck={rateCheck}
          rateCheckBusy={rateCheckBusy}
          onRunRateCheck={runRateCheck}
        />
        <LabelSection
          kind="sample_return"
          title="Sample Return Express Label"
          subtitle="Australia Post Express · Customer → Lab · enclosed INSIDE the kit for the customer's sample"
          Icon={PackageCheck}
          state={state}
          busy={busy}
          onAction={runAction}
          shippingRequired={labelShippingRequired}
        />
      </div>

      {/* Panel footer */}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-3 text-[12px] text-slate-500">
        {state.shipping_last_error && (
          <div className="flex items-center gap-1.5 text-rose-700">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="font-medium">{state.shipping_last_error}</span>
          </div>
        )}
        {!state.shipping_last_error && state.shipping_resolved_error && (
          <div
            className="flex items-center gap-1.5 text-slate-500"
            data-testid="shipping-resolved-error-note"
            title={state.shipping_resolved_error}
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>Previous label error resolved after retry/sync</span>
          </div>
        )}
        <button
          type="button"
          onClick={runDiag}
          disabled={diagBusy}
          data-testid="shipping-carrier-diag-btn"
          className="ml-auto inline-flex items-center gap-1.5 hover:text-slate-800 font-medium disabled:opacity-50"
        >
          {diagBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
          Carrier diagnostic
        </button>
        <button
          type="button"
          onClick={load}
          data-testid="shipping-refresh-btn"
          className="inline-flex items-center gap-1.5 hover:text-slate-800 font-medium"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Carrier diagnostic results */}
      {diag && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="shipping-carrier-diag-results">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
            Carrier entitlement check (read-only — asks Starshipit what this account can ship right now)
          </div>
          {diag.status === "no_credentials" ? (
            <p className="text-[12px] text-rose-700">Starshipit API keys are missing in this environment.</p>
          ) : diag.status === 403 ? (
            <p className="text-[12px] text-slate-700" data-testid="diag-forbidden-note">
              Starshipit refused the diagnostic endpoint (HTTP 403) — this API subscription doesn't include
              the rates/delivery-services scope, so entitlements can't be listed from here. Use the manual
              check instead: in Starshipit open the order and try printing the label directly; Starshipit's
              screen will show the carrier's own error.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${diag.auspost_available ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`} data-testid="diag-auspost-chip">
                  AusPost: {diag.auspost_available ? "available" : "NOT available"}
                </span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${diag.aramex_available ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`} data-testid="diag-aramex-chip">
                  Aramex: {diag.aramex_available ? "available" : "NOT available"}
                </span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">HTTP {diag.status}</span>
              </div>
              {(diag.services || []).length > 0 ? (
                <table className="w-full text-[12px]">
                  <thead><tr className="text-left text-slate-500">
                    <th className="py-1 pr-3">Carrier</th><th className="py-1 pr-3">Code</th><th className="py-1 pr-3">Service</th><th className="py-1">Price</th>
                  </tr></thead>
                  <tbody>
                    {diag.services.map((s, i) => (
                      <tr key={i} className="border-t border-slate-200">
                        <td className="py-1 pr-3 font-medium">{s.carrier_name || s.carrier}</td>
                        <td className="py-1 pr-3 font-mono">{s.service_code}</td>
                        <td className="py-1 pr-3">{s.service_name}</td>
                        <td className="py-1">{s.total_price != null ? `$${Number(s.total_price).toFixed(2)}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-[12px] text-slate-600">
                  Starshipit returned no available services for this route — if AusPost shows NOT available,
                  the AusPost connection in Starshipit (Settings → Couriers) is not active for this account.
                </p>
              )}
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] font-semibold text-slate-500 hover:text-slate-700">Raw response</summary>
                <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-slate-900 text-slate-100 text-[10px] p-3 whitespace-pre-wrap break-all">{JSON.stringify(diag.raw ?? diag, null, 2)}</pre>
              </details>
            </>
          )}
        </div>
      )}
    </section>
  );
}
